import React, { useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Calculator, Copy, Check, Scale, BookOpen, HelpCircle, ShieldCheck, Coins } from "lucide-react";
import { toast } from "sonner";
import {
  CalculationResult,
  calcularSentenciaAdministrativa,
  calcularPrivacionInjusta,
  calcularMoraContratosEstatales
} from "@/lib/calculators/calculatorsEngine";
import { CALCULATOR_FAQS } from "@/lib/calculators/calculatorFaqs";
import { deductCalculatorCredit } from "@/lib/calculators/deductCalculatorCredit";

// 1. Sentencias Administrativas (CPACA)
export const SentenciaAdministrativaForm: React.FC = () => {
  const [capitalIndexar, setCapitalIndexar] = useState("120000000");
  const [costasAgencias, setCostasAgencias] = useState("5000000");
  const [fechaCausacion, setFechaCausacion] = useState("2021-03-10");
  const [ejecutoriaSentencia, setEjecutoriaSentencia] = useState("2023-05-15");
  const [fechaLiquidacion, setFechaLiquidacion] = useState("2026-08-01");
  const [result, setResult] = useState<CalculationResult | null>(null);

  const handleCalculate = async () => {
    const success = await deductCalculatorCredit();
    if (!success) return;

    const cap = parseFloat(capitalIndexar) || 0;
    const d1 = new Date(ejecutoriaSentencia);
    const d2 = new Date(fechaLiquidacion);
    const diffMonths = Math.max(1, Math.round((d2.getTime() - d1.getTime()) / (1000 * 60 * 60 * 24 * 30.44)));

    const res = calcularSentenciaAdministrativa(cap, diffMonths, true);
    setResult(res);
  };

  const faqs = CALCULATOR_FAQS["sentencia-administrativa"] || [];

  return (
    <div className="space-y-8">
      {/* Explanatory Key Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 space-y-1">
          <p className="text-xs font-black text-slate-900 uppercase tracking-wider">Art. 192 CPACA</p>
          <p className="text-xs text-slate-600 leading-relaxed">
            10 meses de plazo de gracia contados desde la ejecutoria antes de incurrir en mora.
          </p>
        </div>
        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 space-y-1">
          <p className="text-xs font-black text-slate-900 uppercase tracking-wider">Interés Simple</p>
          <p className="text-xs text-slate-600 leading-relaxed">
            Anatocismo proscrito. Se convierte TEA a nominal diaria sin capitalización.
          </p>
        </div>
        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 space-y-1">
          <p className="text-xs font-black text-slate-900 uppercase tracking-wider">Datos Oficiales</p>
          <p className="text-xs text-slate-600 leading-relaxed">
            IPC (DANE), DTF (Banco de la República) y Tasa de Usura (Superfinanciera).
          </p>
        </div>
      </div>

      {/* Main Form */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-6 shadow-sm">
        <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider border-b border-slate-100 pb-3 flex items-center gap-2">
          <Scale className="w-4 h-4 text-emerald-600" /> Parámetros de la Liquidación
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Valor Capital a Indexar ($) *</Label>
            <Input
              type="number"
              value={capitalIndexar}
              onChange={(e) => setCapitalIndexar(e.target.value)}
              placeholder="Ej: 50.000.000"
              className="bg-white border-slate-300 text-slate-900 rounded-xl"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Costas y Agencias ($)</Label>
            <Input
              type="number"
              value={costasAgencias}
              onChange={(e) => setCostasAgencias(e.target.value)}
              placeholder="Ej: 5.000.000 (No se indexan)"
              className="bg-white border-slate-300 text-slate-900 rounded-xl"
            />
            <p className="text-[10px] text-slate-500">No se indexan según jurisprudencia del Consejo de Estado.</p>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Fecha Causación / Daño *</Label>
            <Input
              type="date"
              value={fechaCausacion}
              onChange={(e) => setFechaCausacion(e.target.value)}
              className="bg-white border-slate-300 text-slate-900 rounded-xl"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Ejecutoria de la Sentencia *</Label>
            <Input
              type="date"
              value={ejecutoriaSentencia}
              onChange={(e) => setEjecutoriaSentencia(e.target.value)}
              className="bg-white border-slate-300 text-slate-900 rounded-xl"
            />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label className="text-xs font-semibold text-slate-700">Fecha de Liquidación *</Label>
            <Input
              type="date"
              value={fechaLiquidacion}
              onChange={(e) => setFechaLiquidacion(e.target.value)}
              className="bg-white border-slate-300 text-slate-900 rounded-xl"
            />
          </div>
        </div>

        <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-amber-600 shrink-0" />
            <span>Metodología: Los índices IPC y DTF se certifican directamente conforme a las normas vigentes.</span>
          </div>
          <span className="font-extrabold text-amber-900 flex items-center gap-1 shrink-0 ml-2">
            <Coins className="w-3.5 h-3.5 text-amber-700" /> 1 Crédito
          </span>
        </div>

        <Button
          onClick={handleCalculate}
          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-black py-6 rounded-2xl uppercase tracking-wider text-xs shadow-md"
        >
          <Calculator className="w-4 h-4 mr-2" /> Ejecutar Liquidación del Daño (Art. 192 CPACA) — 1 Crédito
        </Button>
      </div>

      {/* Result View */}
      {result && <DetailedResultDisplay result={result} />}

      {/* FAQ Section */}
      {faqs.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4 shadow-sm">
          <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider flex items-center gap-2 border-b border-slate-100 pb-3">
            <HelpCircle className="w-4 h-4 text-emerald-600" /> Preguntas Frecuentes & Marco Normativo
          </h3>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="space-y-1">
                <h4 className="text-xs font-bold text-slate-900">{faq.question}</h4>
                <p className="text-xs text-slate-600 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// 2. Privación Injusta de la Libertad
export const PrivacionInjustaForm: React.FC = () => {
  const [inicioPrivacion, setInicioPrivacion] = useState("2023-01-10");
  const [finPrivacion, setFinPrivacion] = useState("2024-03-15");
  const [salarioVictima, setSalarioVictima] = useState("2500000");
  const [domiciliaria, setDomiciliaria] = useState(false);
  const [conyuge, setConyuge] = useState(true);
  const [numPadresHijos, setNumPadresHijos] = useState("2");
  const [result, setResult] = useState<CalculationResult | null>(null);

  const handleCalculate = async () => {
    const success = await deductCalculatorCredit();
    if (!success) return;

    const d1 = new Date(inicioPrivacion);
    const d2 = new Date(finPrivacion);
    const dias = Math.max(1, Math.round((d2.getTime() - d1.getTime()) / (1000 * 60 * 60 * 24)));
    const sal = parseFloat(salarioVictima) || 1550000;
    const ph = parseInt(numPadresHijos) || 0;

    const res = calcularPrivacionInjusta(dias, sal, { cónyuge: conyuge, padres: ph, hijos: 0 });
    setResult(res);
  };

  const faqs = CALCULATOR_FAQS["privacion-injusta"] || [];

  return (
    <div className="space-y-8">
      {/* Explanatory Key Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 space-y-1">
          <p className="text-xs font-black text-slate-900 uppercase tracking-wider">Jurisprudencia CE</p>
          <p className="text-xs text-slate-600 leading-relaxed">
            Sentencia de unificación Rad. 36009 fija montos de daño moral en SMLMV (15 a 100 SMLMV).
          </p>
        </div>
        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 space-y-1">
          <p className="text-xs font-black text-slate-900 uppercase tracking-wider">Perjuicios a Familiares</p>
          <p className="text-xs text-slate-600 leading-relaxed">
            Cónyuge, padres e hijos reciben indemnización proporcional en 1er grado de consanguinidad.
          </p>
        </div>
        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 space-y-1">
          <p className="text-xs font-black text-slate-900 uppercase tracking-wider">Lucro Cesante</p>
          <p className="text-xs text-slate-600 leading-relaxed">
            Ingreso mensual de la víctima indexado con prestaciones por los meses de detención.
          </p>
        </div>
      </div>

      {/* Main Form */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-6 shadow-sm">
        <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider border-b border-slate-100 pb-3 flex items-center gap-2">
          <Scale className="w-4 h-4 text-emerald-600" /> Parámetros de la Liquidación
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Inicio de la Privación *</Label>
            <Input
              type="date"
              value={inicioPrivacion}
              onChange={(e) => setInicioPrivacion(e.target.value)}
              className="bg-white border-slate-300 text-slate-900 rounded-xl"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Fin / Recuperación de la Libertad *</Label>
            <Input
              type="date"
              value={finPrivacion}
              onChange={(e) => setFinPrivacion(e.target.value)}
              className="bg-white border-slate-300 text-slate-900 rounded-xl"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Ingreso Mensual Víctima ($)</Label>
            <Input
              type="number"
              value={salarioVictima}
              onChange={(e) => setSalarioVictima(e.target.value)}
              className="bg-white border-slate-300 text-slate-900 rounded-xl"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Hijos / Padres (1er Grado - Cantidad)</Label>
            <Input
              type="number"
              value={numPadresHijos}
              onChange={(e) => setNumPadresHijos(e.target.value)}
              className="bg-white border-slate-300 text-slate-900 rounded-xl"
            />
          </div>
        </div>

        <div className="space-y-3 pt-2">
          <div className="flex items-center space-x-2">
            <Checkbox id="cny" checked={conyuge} onCheckedChange={(c) => setConyuge(Boolean(c))} />
            <Label htmlFor="cny" className="text-xs text-slate-700 cursor-pointer">
              Cónyuge / Compañero(a) Permanente
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox id="dom" checked={domiciliaria} onCheckedChange={(c) => setDomiciliaria(Boolean(c))} />
            <Label htmlFor="dom" className="text-xs text-slate-700 cursor-pointer">
              ¿Hubo período en detención domiciliaria?
            </Label>
          </div>
        </div>

        <Button
          onClick={handleCalculate}
          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-black py-6 rounded-2xl uppercase tracking-wider text-xs shadow-md"
        >
          <Calculator className="w-4 h-4 mr-2" /> Calcular Indemnización Reparación Directa — 1 Crédito
        </Button>
      </div>

      {/* Result View */}
      {result && <DetailedResultDisplay result={result} />}

      {/* FAQ Section */}
      {faqs.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4 shadow-sm">
          <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider flex items-center gap-2 border-b border-slate-100 pb-3">
            <HelpCircle className="w-4 h-4 text-emerald-600" /> Preguntas Frecuentes & Marco Normativo
          </h3>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="space-y-1">
                <h4 className="text-xs font-bold text-slate-900">{faq.question}</h4>
                <p className="text-xs text-slate-600 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// 3. Mora Contratos Estatales
export const MoraContratosEstatalesForm: React.FC = () => {
  const [valorObligacion, setValorObligacion] = useState("150000000");
  const [fechaInicioMora, setFechaInicioMora] = useState("2023-06-01");
  const [fechaLiquidacion, setFechaLiquidacion] = useState("2026-08-01");
  const [result, setResult] = useState<CalculationResult | null>(null);

  const handleCalculate = async () => {
    const success = await deductCalculatorCredit();
    if (!success) return;

    const val = parseFloat(valorObligacion) || 0;
    const d1 = new Date(fechaInicioMora);
    const d2 = new Date(fechaLiquidacion);
    const dias = Math.max(1, Math.round((d2.getTime() - d1.getTime()) / (1000 * 60 * 60 * 24)));

    const res = calcularMoraContratosEstatales(val, dias);
    setResult(res);
  };

  const faqs = CALCULATOR_FAQS["mora-contratos-estatales"] || [];

  return (
    <div className="space-y-8">
      {/* Explanatory Key Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 space-y-1">
          <p className="text-xs font-black text-slate-900 uppercase tracking-wider">Ley 80 de 1993</p>
          <p className="text-xs text-slate-600 leading-relaxed">
            Artículo 4 numeral 8 consagra el deber de actualización del valor adeudado.
          </p>
        </div>
        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 space-y-1">
          <p className="text-xs font-black text-slate-900 uppercase tracking-wider">Decreto 679/1994</p>
          <p className="text-xs text-slate-600 leading-relaxed">
            Interés de mora del 1% mensual (12% anual) sobre el valor ajustado por IPC.
          </p>
        </div>
        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 space-y-1">
          <p className="text-xs font-black text-slate-900 uppercase tracking-wider">Sin Capitalización</p>
          <p className="text-xs text-slate-600 leading-relaxed">
            Cálculo de intereses sobre capital actualizado sin generar intereses sobre intereses.
          </p>
        </div>
      </div>

      {/* Main Form */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-6 shadow-sm">
        <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider border-b border-slate-100 pb-3 flex items-center gap-2">
          <Scale className="w-4 h-4 text-emerald-600" /> Parámetros de la Liquidación
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Valor de la Obligación ($) *</Label>
            <Input
              type="number"
              value={valorObligacion}
              onChange={(e) => setValorObligacion(e.target.value)}
              placeholder="Ej: 150.000.000"
              className="bg-white border-slate-300 text-slate-900 rounded-xl"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Fecha Inicial de Mora *</Label>
            <Input
              type="date"
              value={fechaInicioMora}
              onChange={(e) => setFechaInicioMora(e.target.value)}
              className="bg-white border-slate-300 text-slate-900 rounded-xl"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Fecha de Liquidación *</Label>
            <Input
              type="date"
              value={fechaLiquidacion}
              onChange={(e) => setFechaLiquidacion(e.target.value)}
              className="bg-white border-slate-300 text-slate-900 rounded-xl"
            />
          </div>
        </div>

        <Button
          onClick={handleCalculate}
          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-black py-6 rounded-2xl uppercase tracking-wider text-xs shadow-md"
        >
          <Calculator className="w-4 h-4 mr-2" /> Calcular Mora Estatal Ley 80 / Dec 679 — 1 Crédito
        </Button>
      </div>

      {/* Result View */}
      {result && <DetailedResultDisplay result={result} />}

      {/* FAQ Section */}
      {faqs.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4 shadow-sm">
          <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider flex items-center gap-2 border-b border-slate-100 pb-3">
            <HelpCircle className="w-4 h-4 text-emerald-600" /> Preguntas Frecuentes & Marco Normativo
          </h3>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="space-y-1">
                <h4 className="text-xs font-bold text-slate-900">{faq.question}</h4>
                <p className="text-xs text-slate-600 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const DetailedResultDisplay: React.FC<{ result: CalculationResult }> = ({ result }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    const text = `--- INFORME DE LIQUIDACIÓN DEL DAÑO ---
${result.title}
${result.summary}

RESULTADO CONSOLIDADO: $${result.total.toLocaleString("es-CO", { maximumFractionDigits: 0 })} COP

DESGLOSE:
${result.breakdown.map((b) => `- ${b.label}: ${typeof b.value === "number" ? "$" + b.value.toLocaleString("es-CO") : b.value}`).join("\n")}

FUNDAMENTACIÓN NORMATIVA:
${result.normativa.map((n) => `- ${n}`).join("\n")}

Generado en Mi Asistente Legalia`;

    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Informe procesal copiado");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="p-6 rounded-2xl bg-emerald-50/60 border border-emerald-200 space-y-6 text-slate-900 shadow-sm animate-in fade-in">
      <div className="flex justify-between items-start">
        <div>
          <span className="text-[11px] font-extrabold text-emerald-800 uppercase tracking-wider">
            Resumen Consolidado de la Liquidación
          </span>
          <div className="text-3xl md:text-4xl font-black text-slate-900 mt-1">
            ${result.total.toLocaleString("es-CO", { maximumFractionDigits: 0 })} COP
          </div>
          <p className="text-xs text-slate-600 mt-1">{result.summary}</p>
        </div>
        <Button onClick={handleCopy} variant="outline" size="sm" className="bg-white border-slate-300 text-slate-700 hover:bg-slate-50 font-bold">
          {copied ? <Check className="w-4 h-4 text-emerald-600 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
          {copied ? "Copiado!" : "Copiar Informe"}
        </Button>
      </div>

      {/* Breakdown Table */}
      <div className="space-y-2">
        <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-1.5">
          <BookOpen className="w-4 h-4 text-emerald-700" /> Desglose de Conceptos e Intereses
        </h4>
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden divide-y divide-slate-100">
          {result.breakdown.map((b, i) => (
            <div key={i} className="p-3.5 flex justify-between text-xs">
              <span className="text-slate-600 font-medium">{b.label}</span>
              <span className="font-bold text-slate-900">
                {typeof b.value === "number" ? `$${b.value.toLocaleString("es-CO")}` : b.value}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Normativa */}
      <div className="p-4 bg-white rounded-xl border border-slate-200 space-y-2">
        <h5 className="text-xs font-bold text-emerald-800 uppercase tracking-wider">Citas Normativas y Jurisprudencia</h5>
        <ul className="text-xs text-slate-600 list-disc pl-4 space-y-1">
          {result.normativa.map((n, i) => (
            <li key={i}>{n}</li>
          ))}
        </ul>
      </div>
    </div>
  );
};
